import React from "react";
import { JetSportBox } from "../BoxView/JetSportBox";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import LoadingElement from "../loading-element/loading-element";

export function getInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2);
  return `${parts[0][0]}${parts[parts.length - 1][0]}`;
}

interface OrgScreenShellProps {
  title: string;
  subtitle?: string;
  /** Text for the avatar circle; falls back to initials derived from title. */
  avatarText?: string;
  /** Badge chips rendered next to the title (use BadgeChip). */
  badges?: React.ReactNode;
  /** Left navigation (use SectionNavItem). */
  nav: React.ReactNode;
  /** Section content. */
  children: React.ReactNode;
  /** Sticky footer. Omit to render no footer. */
  footer?: {
    onCancel: () => void;
    cancelLabel?: string;
    onSave: () => void;
    saveLabel?: string;
    saveDisabled?: boolean;
    /** Shows the unsaved-changes indicator on the left of the footer. */
    isDirty?: boolean;
    /** Red validation message shown next to the buttons. */
    error?: string;
    /** Extra content rendered between the indicator and the buttons. */
    extra?: React.ReactNode;
  };
  isLoading?: boolean;
}

/**
 * Shared organization-screen shell: header with avatar/name/badges, a left
 * section nav, a content area and a sticky Cancel/Save footer.
 */
export function OrgScreenShell({
  title,
  subtitle,
  avatarText,
  badges,
  nav,
  children,
  footer,
  isLoading,
}: OrgScreenShellProps) {
  return (
    <JetSportBox>
      <div className="flex items-center gap-4 border-b px-8 py-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-10 text-base font-semibold uppercase text-primary-100">
          {avatarText || getInitials(title)}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-lg font-semibold text-gray-800">
              {title || "—"}
            </h1>
            {badges}
          </div>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
      <div className="flex min-h-[calc(100vh-21rem)] flex-1 items-stretch">
        <aside className="w-56 shrink-0 space-y-1 border-r px-3 py-4">
          {nav}
        </aside>
        <div className="min-w-0 flex-1 px-8 py-6">{children}</div>
      </div>
      {footer && (
        <div className="sticky bottom-0 z-10 flex items-center rounded-b-md border-t bg-white px-8 py-4">
          <div className="flex-1">
            {footer.isDirty && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600">
                <span className="h-2 w-2 rounded-full bg-amber-500" />
                Unsaved changes
              </span>
            )}
          </div>
          {footer.error && (
            <span className="mr-4 max-w-lg text-xs font-medium text-red-100">
              {footer.error}
            </span>
          )}
          {footer.extra}
          <div className="flex items-center space-x-4">
            <SecondaryButton onClick={footer.onCancel}>
              {footer.cancelLabel ?? "Cancel"}
            </SecondaryButton>
            <PrimaryButton
              onClick={footer.onSave}
              disabled={footer.saveDisabled}
            >
              {footer.saveLabel ?? "Save changes"}
            </PrimaryButton>
          </div>
        </div>
      )}
      {isLoading && <LoadingElement className={"rounded"} transparent />}
    </JetSportBox>
  );
}
